"use client"

import { useQuery } from "@tanstack/react-query"
import { withTimeout } from "@/lib/abort-signal"
import type { AppUser } from "@/lib/auth/types"
import type { StoredPrompt } from "@/lib/prompts/stored-prompts"
import { fetchWithTrace } from "@/lib/fetch-with-trace"
import { Logger } from "@/lib/logger-client"
import { fetchWithRefreshConflictRetry } from "@/lib/auth/refresh-conflict"

export interface ExtendedUser extends AppUser {
  isSubscribed?: boolean
  currentPlan?: string | null
}

export interface SubscriptionRow {
  auto_mode_enabled?: boolean
  status?: string
  plan_name?: string | null
  plan_type?: string | null
  start_date?: string | null
  end_date?: string | null
  // Normalised to numbers at the /api/user/me boundary — the DB column is
  // numeric(6,2) and the driver serialises it as a string ("0.00").
  credits?: number | null
  interview_call_credits?: number | null
  programming_language?: string | null
  personalize_prompt?: string | StoredPrompt[] | null
  // ── Billing ────────────────────────────────────────────────────────────
  // 'one_time' (a purchase that does not renew) or 'subscription' (a live
  // mandate). This — never plan_name — is what decides whether the Billing tab
  // offers a cancel control: a one-time monthly buyer and a monthly subscriber
  // have identical plan_name and end_date.
  billing_mode?: string | null
  /** Scheduled to end at end_date. Access is unaffected until then. */
  cancel_at_period_end?: boolean | null
  /** Provider billing date. A mirror, NOT the entitlement source. */
  current_period_end?: string | null
  /** Raw Dodo or Stripe subscription status. */
  provider_subscription_status?: string | null
  payment_provider?: string | null
  /**
   * Can the provider still charge? Computed server-side in /api/user/me, because the
   * mandate id it derives from is deliberately not sent to the browser. NOT the
   * same question as billing_mode — a downgraded row keeps a live mandate.
   */
  has_live_mandate?: boolean | null
}

interface UserQueryResult {
  user: ExtendedUser | null
  subscription: SubscriptionRow | null
  isSubscribed: boolean
  currentPlan: string | null
  isAdmin: boolean
}

export const SIGNED_OUT: UserQueryResult = {
  user: null,
  subscription: null,
  isSubscribed: false,
  currentPlan: null,
  isAdmin: false,
}

/**
 * A genuinely signed-out visitor gets HTTP **200** with `user: null` from
 * /api/user/me (see app/api/user/me/route.ts). A non-OK status therefore always
 * means an infrastructure problem, never a sign-out — so it must NOT resolve to
 * a signed-out-looking value. It used to, and because a resolved value is a
 * success as far as React Query is concerned, `retry` never fired and no error
 * state ever surfaced: one 502 or Wi-Fi blip made /dashboard and /account push a
 * still-authenticated, paying user to /signin, where middleware saw the valid
 * cookies and bounced them again onto the marketing homepage. Throwing instead
 * lets React Query retry with backoff and lets pages tell "signed out" apart
 * from "request failed".
 */
async function fetchUserAndSubscription({
  signal,
}: {
  signal: AbortSignal
}): Promise<UserQueryResult> {
  // withTimeout, not AbortSignal.any: the bare static throws synchronously on
  // Chrome < 116 / Safari < 17.4, which surfaced here as every visitor on
  // those engines rendering as errored/unauthenticated (lib/abort-signal.ts).
  const requestSignal = withTimeout(signal, 10_000)
  const res = await fetchWithRefreshConflictRetry(async () => {
    const { response } = await fetchWithTrace("/api/user/me", {
      credentials: "include",
      signal: requestSignal,
      logger: Logger(),
    })
    return response
  }, requestSignal)
  if (!res.ok) {
    throw new Error(`user_me_request_failed:${res.status}`)
  }

  const data = await res.json()
  if (!data.user) return SIGNED_OUT

  return {
    user: { ...data.user, isSubscribed: data.isSubscribed, currentPlan: data.currentPlan },
    subscription: (data.subscription ?? null) as SubscriptionRow | null,
    isSubscribed: data.isSubscribed ?? false,
    currentPlan: data.currentPlan ?? null,
    isAdmin: data.isAdmin ?? false,
  }
}

export function useUser() {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["user"],
    queryFn: fetchUserAndSubscription,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
    // Ride out a transient 5xx / network blip before the UI ever concludes
    // anything about this user. Session validity lives in httpOnly cookies, so
    // retrying costs nothing but a round trip.
    retry: 2,
  })

  return {
    user: data?.user ?? null,
    subscription: data?.subscription ?? null,
    isSubscribed: data?.isSubscribed ?? false,
    currentPlan: data?.currentPlan ?? null,
    isAdmin: data?.isAdmin ?? false,
    loading: isLoading,
    /**
     * The request failed after retries. This is NOT a sign-out — the cookies may
     * still be perfectly valid. Never navigate to /signin on this; show an error
     * with `refetch` instead.
     */
    isError,
    /** True once we know, from a successful response, that nobody is signed in. */
    signedOut: !isLoading && !isError && data?.user == null,
    refetching: isFetching,
    refetch,
  }
}
